import { useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import type { UseFormReturn, Control } from "react-hook-form";
import { usePage } from "@inertiajs/react";
import { useAdsChatSubmit } from "./index";
import { useFormRegistration } from "@hooks/useFormRegistration";
import { useDebouncedSave } from "@hooks/useDebouncedSave";
import { createRefreshHandler } from "../utils/refreshAssets";
import { Ads, generateUUID } from "@shared";
import type { UpdateCampaignRequestBody } from "@rails_api_base";

type AssetArrayKey = "headlines" | "descriptions" | "callouts" | "keywords" | "negative_keywords";

interface AdsPageProps {
  campaign?: { id: number; [key: string]: unknown };
  headlines?: Array<{ id?: number; text: string; position?: number; locked?: boolean }>;
  descriptions?: Array<{ id?: number; text: string; position?: number; locked?: boolean }>;
  callouts?: Array<{ id?: number; text: string; position?: number; locked?: boolean }>;
  keywords?: Array<{ id?: number; text: string; match_type?: string; position?: number; locked?: boolean; negative?: boolean }>;
  negative_keywords?: Array<{ id?: number; text: string; match_type?: string; position?: number; locked?: boolean; negative?: boolean }>;
  [key: string]: unknown;
}

// Min-count defaults for the asset UI grid. negative_keywords is a frontend-only
// partition of keywords — it isn't a distinct AssetKind in the shared prompt system,
// so we use a local fallback table instead of Ads.DefaultNumAssets.
const AssetMinCount: Record<AssetArrayKey, number> = {
  headlines: 6,
  descriptions: 4,
  callouts: 6,
  keywords: 8,
  negative_keywords: 0,
};

interface UseAssetFormConfig {
  assetKey: AssetArrayKey;
  formGroup: string;
  schema: z.ZodSchema;
  transformFn: (data: any) => Partial<UpdateCampaignRequestBody> | null;
  refreshStage?: string;
}

interface UseAssetFormReturn {
  addAsset: (text: string) => void;
  removeAsset: (index: number) => void;
  updateAsset: (index: number, text: string) => void;
  lockToggle: (fieldName: string, index: number) => Promise<void>;
  refreshAssets: () => Promise<void>;
  filteredAssets: Ads.Asset[];
  fields: Ads.Asset[];
  isPlaceholder: boolean;
  methods: UseFormReturn<any>;
  control: Control<any>;
  getData: () => Partial<UpdateCampaignRequestBody> | null;
}

/**
 * Generates stable placeholder IDs for empty asset slots.
 * Uses deterministic IDs so the fingerprint doesn't thrash across renders.
 */
function createPlaceholderAssets(assetKey: AssetArrayKey): Ads.Asset[] {
  const count = AssetMinCount[assetKey] ?? 4;
  return Array.from({ length: count }, (_, i) => ({
    id: `placeholder-${assetKey}-${i}`,
    text: "",
    locked: false,
  }));
}

/**
 * Ensures the asset array has at least DefaultNumAssets entries.
 * Pads with empty placeholders so the UI always shows the minimum number of input fields.
 */
function padToMinimum(assets: Ads.Asset[], assetKey: AssetArrayKey): Ads.Asset[] {
  const minCount = AssetMinCount[assetKey] ?? 1;
  if (assets.length >= minCount) return assets;
  const padding = Array.from({ length: minCount - assets.length }, (_, i) => ({
    id: `placeholder-${assetKey}-pad-${i}`,
    text: "",
    locked: false,
  }));
  return [...assets, ...padding];
}

/**
 * Converts saved asset data (from Inertia props) to form-compatible Ads.Asset format.
 * Reads `locked` from backend — persisted across sessions.
 */
function toFormAssets(
  savedAssets: Array<{ id?: number; text: string; locked?: boolean; [key: string]: unknown }> | null | undefined,
): Ads.Asset[] {
  if (!savedAssets?.length) return [];
  return savedAssets.map((a) => {
    const id = a.id != null ? String(a.id) : generateUUID();
    return {
      id,
      text: a.text,
      locked: a.locked ?? false,
    };
  });
}

export function useAssetForm({
  assetKey,
  formGroup,
  schema,
  transformFn,
  refreshStage,
}: UseAssetFormConfig): UseAssetFormReturn {
  const page = usePage<AdsPageProps>();
  const savedAssets = page.props[assetKey] as AdsPageProps[AssetArrayKey];
  const campaignId = page.props.campaign?.id;

  const hasSavedAssets = !!savedAssets?.length;
  const filteredAssets = hasSavedAssets
    ? padToMinimum(toFormAssets(savedAssets), assetKey)
    : createPlaceholderAssets(assetKey);
  const prevFingerprintRef = useRef("");

  const methods = useForm({
    resolver: zodResolver(schema as any) as any,
    mode: "onChange",
    defaultValues: {
      [assetKey]: filteredAssets,
    },
  });

  const transformFnRef = useRef(transformFn);
  transformFnRef.current = transformFn;

  const { save, saveNow, cancel } = useDebouncedSave({ campaignId });

  const getData = useCallback((): Partial<UpdateCampaignRequestBody> | null => {
    const formData = methods.getValues();
    return transformFnRef.current(formData);
  }, [methods]);

  // Sync Inertia props → RHF when assets change (IDs or content, e.g. after router.reload())
  const currentFingerprint = JSON.stringify(filteredAssets.map((a) => [a.id, a.text, a.locked]));
  if (currentFingerprint !== prevFingerprintRef.current) {
    methods.reset({ [assetKey]: filteredAssets });
    prevFingerprintRef.current = currentFingerprint;
  }

  const submit = useAdsChatSubmit();

  const addAsset = useCallback(
    (text: string) => {
      const current = methods.getValues(assetKey) as Ads.Asset[];
      const newAsset: Ads.Asset = {
        id: generateUUID(),
        text,
        locked: true,
      };
      const updated = [...current, newAsset];
      methods.reset({ [assetKey]: updated });
      const apiData = transformFnRef.current({ [assetKey]: updated });
      if (apiData) saveNow(apiData);
    },
    [methods, assetKey, saveNow]
  );

  const removeAsset = useCallback(
    (index: number) => {
      const current = methods.getValues(assetKey) as Ads.Asset[];
      const minCount = AssetMinCount[assetKey] ?? 1;

      let updated: Ads.Asset[];
      if (current.length > minCount) {
        // Above minimum: remove the field entirely
        updated = current.filter((_, i) => i !== index);
      } else {
        // At or below minimum: clear the text but keep the slot
        updated = current.map((a, i) =>
          i === index ? { ...a, id: generateUUID(), text: "", locked: false } : a
        );
      }

      methods.reset({ [assetKey]: updated });
      const apiData = transformFnRef.current({ [assetKey]: updated });
      if (apiData) saveNow(apiData);
    },
    [methods, assetKey, saveNow]
  );

  const updateAsset = useCallback(
    (index: number, text: string) => {
      const current = methods.getValues(assetKey) as Ads.Asset[];
      const updated = current.map((a, i) => (i === index ? { ...a, text } : a));
      methods.reset({ [assetKey]: updated });
      const apiData = transformFnRef.current({ [assetKey]: updated });
      if (apiData) save(apiData);
    },
    [methods, assetKey, save]
  );

  // Lock toggle must be durable in Rails before any subsequent refresh, since
  // the ads graph's loadAssets node reads locks from Rails — not from the
  // graph checkpoint or a submit-time payload. Awaiting saveNow ensures the
  // PATCH has settled before control returns to the caller.
  const lockToggle = useCallback(
    async (_fieldName: string, index: number) => {
      const current = methods.getValues(assetKey) as Ads.Asset[];
      const asset = current[index];
      if (!asset) return;

      // Can't lock an empty input
      if (!asset.locked && !asset.text) {
        methods.setError(`${assetKey}.${index}.text` as any, {
          type: "manual",
          message: "Cannot lock an empty input.",
        });
        return;
      }

      const updated = current.map((a, i) =>
        i === index ? { ...a, locked: !a.locked } : a
      );
      methods.reset({ [assetKey]: updated });
      const apiData = transformFnRef.current({ [assetKey]: updated });
      if (apiData) await saveNow(apiData);
    },
    [methods, assetKey, saveNow]
  );

  // Flush pending edits (debounced or in-flight) to Rails before firing the
  // refresh intent. The graph's loadAssets node reads the authoritative state
  // from Rails; this await closes the race where a user edits or locks an
  // asset and immediately clicks refresh before the PATCH has landed.
  const refreshAssets = useCallback(async () => {
    if (refreshStage && submit && assetKey !== "negative_keywords") {
      const current = methods.getValues(assetKey) as Ads.Asset[];
      const apiData = transformFnRef.current({ [assetKey]: current });
      if (apiData) await saveNow(apiData);
      createRefreshHandler(assetKey, current, submit, refreshStage);
    }
  }, [refreshStage, submit, assetKey, methods, saveNow]);

  useFormRegistration(formGroup, methods, getData, cancel);

  // Derive fields from RHF state so Controllers match form values after mutations
  const watchedAssets = methods.watch(assetKey) as Ads.Asset[] | undefined;
  const fields = (watchedAssets?.length ? watchedAssets : filteredAssets).map((a) => ({ ...a, id: a.id }));

  return {
    addAsset,
    removeAsset,
    updateAsset,
    lockToggle,
    refreshAssets,
    filteredAssets,
    fields,
    isPlaceholder: !hasSavedAssets,
    methods,
    control: methods.control,
    getData,
  };
}
